import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../ValidationHub.css';
import { apiFetch } from '../utils/api';
// DISABLED: Bulk validation carousel temporarily disabled due to invoice numbering issues
// When multiple invoices have the same number (e.g., FACT00005), the cascade update
// happens only after submission, but the user needs to see the updated numbers (FACT00006)
// BEFORE submitting. Until this is resolved, use individual invoice approval instead.
// import ValidationCarouselModal from '../components/ValidationCarousel/ValidationCarouselModal';
import AnafStatusButton from '../components/AnafStatusButton';
import BrandLogo from '../components/shared/BrandLogo';

// Searchable dropdown component for client filter
const SearchableClientDropdown = ({ clients, value, onChange, placeholder = "Caută emitent..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  // Find selected client name for display
  const selectedClient = clients.find(c => String(c.id) === String(value));

  // Filter clients based on search term
  const filteredClients = clients.filter(client =>
    client.nume.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Build options list: "Toți emitenții" + filtered clients
  const options = [
    { id: '', nume: 'Toți emitenții' },
    ...filteredClients
  ];

  // Reset highlighted index when search term or dropdown state changes
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(-1);
    }
  }, [searchTerm, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && menuRef.current) {
      const items = menuRef.current.querySelectorAll('.searchable-dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Handle selection
  const handleSelect = (clientId) => {
    onChange(clientId);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Handle clear
  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      // Open dropdown on ArrowDown or Enter when closed
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          handleSelect(options[highlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className="searchable-dropdown"
      ref={containerRef}
      onKeyDown={!isOpen ? handleKeyDown : undefined}
      tabIndex={isOpen ? -1 : 0}
    >
      <div
        className={`searchable-dropdown-trigger ${isOpen ? 'open' : ''} ${value ? 'has-value' : ''}`}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setTimeout(() => inputRef.current?.focus(), 50);
          }
        }}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            className="searchable-dropdown-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={placeholder}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <span className={`searchable-dropdown-display ${!value ? 'placeholder' : ''}`}>
            {selectedClient ? selectedClient.nume : 'Toți emitenții'}
          </span>
        )}
        <div className="searchable-dropdown-icons">
          {value && !isOpen && (
            <button
              className="searchable-dropdown-clear"
              onClick={handleClear}
              title="Șterge selecția"
            >
              ✕
            </button>
          )}
          <span className={`searchable-dropdown-arrow ${isOpen ? 'open' : ''}`}>▾</span>
        </div>
      </div>

      {isOpen && (
        <div className="searchable-dropdown-menu" ref={menuRef}>
          {options.map((option, index) => (
            <div
              key={option.id || 'all'}
              className={`searchable-dropdown-item ${
                String(option.id) === String(value) ? 'selected' : ''
              } ${highlightedIndex === index ? 'highlighted' : ''}`}
              onClick={() => handleSelect(option.id)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {option.nume}
            </div>
          ))}
          {filteredClients.length === 0 && (
            <div className="searchable-dropdown-empty">
              Niciun emitent găsit
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Professional status indicator
const ValidationStatus = ({ status }) => {
  const statusConfig = {
    GREEN: {
      cardClass: 'status-card status-card-green',
      iconClass: 'status-icon status-icon-green',
      labelClass: 'status-label status-label-green',
      descClass: 'status-description status-description-green',
      label: 'COMPLETĂ',
      description: 'Agentul contAI a considerat că are toate informațiile necesare pentru a crea această factură.'
    },
    YELLOW: {
      cardClass: 'status-card status-card-yellow',
      iconClass: 'status-icon status-icon-yellow',
      labelClass: 'status-label status-label-yellow',
      descClass: 'status-description status-description-yellow',
      label: 'ATENȚIE',
      description: 'Agentul contAI a identificat câteva informații care ar trebui verificate sau completate pentru această factură.'
    },
    RED: {
      cardClass: 'status-card status-card-red',
      iconClass: 'status-icon status-icon-red',
      labelClass: 'status-label status-label-red',
      descClass: 'status-description status-description-red',
      label: 'EROARE',
      description: 'Agentul contAI a identificat informații lipsă sau nevalide care trebuie corectate pentru această factură.'
    },
  };

  const config = statusConfig[status] || statusConfig['YELLOW'];

  return (
    <div className={config.cardClass}>
      <div className="status-header">
        <div className="status-indicator">
          <div className={config.iconClass}></div>
          <span className={config.labelClass}>
            {config.label}
          </span>
        </div>
      </div>
      <p className={config.descClass}>
        {config.description}
      </p>
    </div>
  );
};

// Professional data table (status filters moved to header)
const InvoiceValidationTable = ({ invoices, activeFilter, currentPage }) => {
  const filteredInvoices = invoices;

  return (
    <div className="invoice-table-container">
      {/* Main Grid */}
      <div className="invoice-grid">
        <div className="invoice-grid-header">
          <span className="col col-status">Status</span>
          <span className="col col-parties">Emitent / Cumpărător</span>
          <span className="col col-meta">Factură</span>
          <span className="col col-amount">Sumă</span>
          <span className="col col-actions">Acțiuni</span>
        </div>

        {filteredInvoices.map((invoice) => (
          <div key={invoice.id} className="invoice-grid-row">
            <div className="col col-status">
              <ValidationStatus
                status={invoice.confidence_flag}
                observations={invoice.observatii_validare}
              />
            </div>

            <div className="col col-parties">
              <div className="party-block">
                <span className="party-label">Emitent</span>
                <span className="party-name">{invoice.seller_snapshot?.nume || 'Nume necunoscut'}</span>
                <span className="party-email">{invoice.seller_snapshot?.email || 'N/A'}</span>
              </div>
              <div className="party-block">
                <span className="party-label">Cumpărător</span>
                <span className="party-name">{invoice.buyer_snapshot?.nume || 'Nume necunoscut'}</span>
              </div>
            </div>

            <div className="col col-meta">
              <div className="meta-item">
                <span className="meta-label">Număr</span>
                <span className="meta-value">{invoice.numar_factura}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Data</span>
                <span className="meta-value">{invoice.data_emitere}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Plată</span>
                <span className="meta-value">{invoice.modalitate_plata || 'ORDIN DE PLATA'}</span>
              </div>
            </div>

            <div className="col col-amount">
              <span className="invoice-amount">
                {parseFloat(invoice.total_general || 0).toLocaleString('ro-RO', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })} {invoice.moneda}
              </span>
            </div>

            <div className="col col-actions">
              <div className="actions-stack">
                <Link
                to={`/invoice/${invoice.id}`}
                state={{
                  from: 'validation',
                  returnState: {
                    filter: activeFilter,
                    page: currentPage,
                    scrollY: window.scrollY
                  }
                }}
                className="review-btn"
              >
                  Revizuire
                </Link>
                {String(invoice.modalitate_plata || '').toLowerCase() === 'numerar' && (
                  <span className="receipt-indicator">Chitanță</span>
                )}
              </div>
            </div>

            <div className="col col-lines">
              <div className="line-preview">
                {(invoice.line_preview || []).map((line, idx) => (
                  <div key={idx} className="line-preview-item">
                    <span className="line-preview-name">
                      {line.description || 'Produs fără denumire'}
                    </span>
                    <span className="line-preview-meta">
                      {`${line.quantity ?? 0} ${line.unit === 'H87' ? 'BUC' : (line.unit || 'H87')}`}
                    </span>
                  </div>
                ))}
                {invoice.line_count > (invoice.line_preview?.length || 0) && (
                  <div className="line-preview-more">
                    {`+${invoice.line_count - (invoice.line_preview?.length || 0)} linii`}
                  </div>
                )}
                {!invoice.line_preview?.length && (
                  <div className="line-preview-empty">Nu există detalii produs în preview.</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Loading state
const LoadingState = () => (
  <div className="loading-container">
    <div className="loading-header">
      <div className="skeleton-box skeleton-title"></div>
    </div>
    <div className="loading-content">
      {[1, 2, 3].map(i => (
        <div key={i} className="loading-skeleton">
          <div className="skeleton-box skeleton-status"></div>
          <div className="skeleton-box skeleton-name"></div>
          <div className="skeleton-box skeleton-email"></div>
          <div className="skeleton-box skeleton-name"></div>
          <div className="skeleton-box skeleton-number"></div>
          <div className="skeleton-box skeleton-date"></div>
          <div className="skeleton-box skeleton-amount"></div>
          <div className="skeleton-box skeleton-action"></div>
        </div>
      ))}
    </div>
  </div>
);

// Custom hook to fetch clients for filter dropdown
const useClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await apiFetch('/api/clients?page_size=100&sort=nume.asc');
        if (response.ok) {
          const data = await response.json();
          setClients(data.items || []);
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  return { clients, loading };
};

// Custom hook to fetch invoices with filter support
const useInvoices = (activeFilter, filters = {}, initialPage = 1) => {
  const [invoices, setInvoices] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statusCounts, setStatusCounts] = useState({ GREEN: 0, YELLOW: 0, RED: 0, SENT: 0 });

  const fetchInvoices = async (pageNum, statusFilter, currentFilters = {}, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setStatus('loading');
    }

    try {
      // Build query parameters based on filter
      let url = `/api/invoices/validation?page=${pageNum}&page_size=10`;

      if (statusFilter === 'SENT') {
        url += '&sent=true&status=ALL';
      } else if (statusFilter === 'ALL') {
        url += '&sent=false&status=ALL';
      } else {
        url += `&sent=false&status=${statusFilter}`;
      }

      // Add additional filters
      if (currentFilters.clientId) {
        url += `&client_id=${currentFilters.clientId}`;
      }
      if (currentFilters.dateFrom) {
        url += `&date_from=${currentFilters.dateFrom}`;
      }
      if (currentFilters.dateTo) {
        url += `&date_to=${currentFilters.dateTo}`;
      }

      console.log('🔍 Fetching invoices with URL:', url);
      console.log('🔍 Current filters:', currentFilters);

      const response = await apiFetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      // Update state with paginated response
      if (append) {
        setInvoices(prev => [...prev, ...data.invoices]);
      } else {
        setInvoices(data.invoices);
      }

      setHasNext(data.has_next);
      setTotal(data.total);
      setPage(data.page);
      setStatusCounts(data.status_counts || { GREEN: 0, YELLOW: 0, RED: 0, SENT: 0 });
      setStatus('success');
      setLoadingMore(false);
    } catch (err) {
      setError(err.message);
      setStatus('error');
      setLoadingMore(false);
      console.error('Error fetching invoices:', err);
    }
  };

  const loadMore = () => {
    if (hasNext && !loadingMore) {
      fetchInvoices(page + 1, activeFilter, filters, true);
    }
  };

  const refetchFromStart = (newFilter, newFilters) => {
    setPage(1);
    fetchInvoices(1, newFilter || activeFilter, newFilters || filters, false);
  };

  // Load multiple pages on initial mount if needed (for state restoration)
  const loadMultiplePages = async (targetPage, statusFilter, currentFilters = {}) => {
    setStatus('loading');
    let allInvoices = [];

    try {
      // Load pages 1 through targetPage sequentially
      for (let pageNum = 1; pageNum <= targetPage; pageNum++) {
        let url = `/api/invoices/validation?page=${pageNum}&page_size=10`;

        if (statusFilter === 'SENT') {
          url += '&sent=true&status=ALL';
        } else if (statusFilter === 'ALL') {
          url += '&sent=false&status=ALL';
        } else {
          url += `&sent=false&status=${statusFilter}`;
        }

        // Add additional filters
        if (currentFilters.clientId) {
          url += `&client_id=${currentFilters.clientId}`;
        }
        if (currentFilters.dateFrom) {
          url += `&date_from=${currentFilters.dateFrom}`;
        }
        if (currentFilters.dateTo) {
          url += `&date_to=${currentFilters.dateTo}`;
        }

        const response = await apiFetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();

        allInvoices = [...allInvoices, ...data.invoices];

        // Update state with final values from last page
        if (pageNum === targetPage) {
          setInvoices(allInvoices);
          setHasNext(data.has_next);
          setTotal(data.total);
          setPage(data.page);
          setStatusCounts(data.status_counts || { GREEN: 0, YELLOW: 0, RED: 0, SENT: 0 });
          setStatus('success');
        }
      }
    } catch (err) {
      setError(err.message);
      setStatus('error');
      console.error('Error loading multiple pages:', err);
    }
  };

  useEffect(() => {
    if (initialPage > 1) {
      // Restore state: load all pages up to initialPage
      loadMultiplePages(initialPage, activeFilter, filters);
    } else {
      // Normal load: just load page 1
      fetchInvoices(1, activeFilter, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, filters.clientId, filters.dateFrom, filters.dateTo]);

  return { invoices, status, error, hasNext, total, loadMore, loadingMore, statusCounts, refetch: refetchFromStart, currentPage: page };
};

// Main component
export default function ValidationHubPage() {
  const location = useLocation();

  // Restore state from navigation if returning from invoice detail page
  const returnState = location.state?.returnState;
  const [activeFilter, setActiveFilter] = useState(returnState?.filter || 'ALL');
  const initialPage = returnState?.page || 1;

  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState({
    clientId: '',
    dateFrom: '',
    dateTo: ''
  });

  // Fetch clients for dropdown
  const { clients } = useClients();

  const { invoices, status, error, hasNext, total, loadMore, loadingMore, statusCounts, refetch, currentPage } = useInvoices(activeFilter, advancedFilters, initialPage);
  const [isDevelopment, setIsDevelopment] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  // DISABLED: Bulk validation carousel - see import comment for details
  // const [showValidationCarousel, setShowValidationCarousel] = useState(false);
  const [currentUsername, setCurrentUsername] = useState(null);
  const [bnrRate, setBnrRate] = useState(null);

  // Restore scroll position after data loads
  useEffect(() => {
    if (returnState?.scrollY && status === 'success' && invoices.length > 0) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        window.scrollTo({
          top: returnState.scrollY,
          behavior: 'instant' // Use 'instant' to avoid animation
        });
        // Clear the return state from history to prevent scroll on future visits
        window.history.replaceState({}, document.title);
      }, 100);
    }
  }, [status, invoices.length, returnState]);

  // Check if we're in development mode
  useEffect(() => {
    const checkDevelopmentMode = async () => {
      try {
        const response = await apiFetch('/api/config/is-development');
        if (response.ok) {
          const data = await response.json();
          setIsDevelopment(data.isDevelopment);
        }
      } catch (err) {
        console.error('Error checking development mode:', err);
      }
    };
    checkDevelopmentMode();
  }, []);

  // Fetch BNR exchange rate (previous day's rate, matching conversion logic)
  useEffect(() => {
    const fetchBnrRate = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const response = await apiFetch(`/api/bnr/rate?date=${today}`);
        if (response.ok) {
          const data = await response.json();
          setBnrRate(data);
        }
      } catch (err) {
        console.error('Error fetching BNR rate:', err);
      }
    };
    fetchBnrRate();
  }, []);

  // Fetch current user to check if admin (for ANAF button visibility)
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await apiFetch('/api/auth/me');
        console.log('🔍 Response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Current user fetched:', data);
          console.log('✅ Username:', data.username);
          console.log('✅ Is admin?', data.username === 'admin');
          setCurrentUsername(data.username);
        } else {
          console.error('❌ Failed to fetch user, status:', response.status);
        }
      } catch (err) {
        console.error('❌ Error fetching current user:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleTestEmail = async () => {
    setTestLoading(true);
    try {
      const response = await apiFetch('/test-latest-email');
      if (response.ok) {
        const data = await response.json();
        alert('Email processing completed!\n\n' + JSON.stringify(data, null, 2));
        refetch(); // Refresh the invoice list
      } else {
        const errorData = await response.json();
        alert('Error: ' + (errorData.message || 'Failed to process email'));
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setTestLoading(false);
    }
  };

  // DISABLED: Bulk validation carousel handlers - see import comment for details
  // const handleOpenValidationCarousel = () => {
  //   setShowValidationCarousel(true);
  // };

  // const handleCloseValidationCarousel = () => {
  //   setShowValidationCarousel(false);
  // };

  // const handleValidationComplete = () => {
  //   // Refresh the invoice list after validation is complete
  //   refetch();
  // };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return <LoadingState />;
      case 'error':
        return (
          <div className="error-container">
            <div className="error-icon">
              <div className="error-icon-dot"></div>
            </div>
            <h3 className="error-title">Eroare de Conectare</h3>
            <p className="error-message">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="error-btn"
            >
              Reîncarcă Pagina
            </button>
          </div>
        );
      case 'success':
        return invoices.length > 0 ? (
          <>
            <InvoiceValidationTable
              invoices={invoices}
              activeFilter={activeFilter}
              currentPage={currentPage}
            />

            {/* Pagination Info & Load More Button */}
            <div className="pagination-container" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
              padding: '2rem 0',
              marginTop: '1rem'
            }}>
              {/* Show count info */}
              <div style={{
                fontSize: '0.875rem',
                color: '#746c5a',
                textAlign: 'center'
              }}>
                Se afișează {invoices.length} din {total} facturi {activeFilter !== 'ALL' ? `(${activeFilter})` : ''}
              </div>

              {/* Load More Button */}
              {hasNext && (
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="load-more-btn"
                  style={{
                    padding: '0.75rem 2rem',
                    backgroundColor: loadingMore ? '#e2ded3' : '#b09244',
                    color: loadingMore ? '#a69e8e' : 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: loadingMore ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loadingMore) {
                      e.target.style.backgroundColor = '#9d8136';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loadingMore) {
                      e.target.style.backgroundColor = '#b09244';
                    }
                  }}
                >
                  {loadingMore ? 'Se încarcă...' : 'Încarcă mai multe'}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="empty-container">
            <div className="empty-icon">
              <div className="empty-icon-dot"></div>
            </div>
            <h3 className="empty-title">
              Toate facturile sunt procesate
            </h3>
            <p className="empty-message">
              Nu există facturi care necesită validare în acest moment.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  // Check if any filters are active
  const hasActiveFilters = advancedFilters.clientId || advancedFilters.dateFrom || advancedFilters.dateTo;

  return (
    <div className="validation-hub">
      {/* Header */}
      <header className="validation-header">
        <div className="validation-header-content">
          <div className="validation-heading brand-heading">
            <BrandLogo className="brand-logo" size={46} />
            <div className="brand-text">
              <h1 className="brand-title">
                cont<span className="brand-title-accent">.AI</span>
              </h1>
              <p className="brand-subtitle">
                agenți AI pentru facturare
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="header-links">
              <Link to="/emails" className="header-link">Emailuri</Link>
              <Link to="/clients" className="header-link">Emitenți</Link>
              <Link to="/anaf-invoices" className="header-link">ANAF</Link>
              {isDevelopment && (
                <button
                  onClick={handleTestEmail}
                  className="header-link"
                  disabled={testLoading}
                  style={{
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    cursor: testLoading ? 'not-allowed' : 'pointer',
                    opacity: testLoading ? 0.6 : 1
                  }}
                >
                  {testLoading ? 'Procesare...' : 'Test Email'}
                </button>
              )}
              <button onClick={handleLogout} className="logout-btn">Deconectare</button>
            </div>
            {(() => {
              const allowedUsers = ['admin', 'contab'];
              const canAccessAnaf = allowedUsers.includes(currentUsername);
              return canAccessAnaf && <AnafStatusButton />;
            })()}
          </div>
        </div>

        {/* Integrated Filters Row - Status tabs + Inline filters */}
        <div className="header-filters-row">
          {/* Status filter tabs on the left */}
          <div className="status-tabs">
            <button
              className={`status-tab ${activeFilter === 'ALL' ? 'active' : ''}`}
              onClick={() => handleFilterChange('ALL')}
            >
              Toate
              <span className="tab-count">{statusCounts.GREEN + statusCounts.YELLOW + statusCounts.RED}</span>
            </button>
            <button
              className={`status-tab ${activeFilter === 'GREEN' ? 'active' : ''}`}
              onClick={() => handleFilterChange('GREEN')}
            >
              <span className="tab-dot tab-dot-green"></span>
              Complete
              <span className="tab-count">{statusCounts.GREEN}</span>
            </button>
            <button
              className={`status-tab ${activeFilter === 'YELLOW' ? 'active' : ''}`}
              onClick={() => handleFilterChange('YELLOW')}
            >
              <span className="tab-dot tab-dot-yellow"></span>
              Atenție
              <span className="tab-count">{statusCounts.YELLOW}</span>
            </button>
            <button
              className={`status-tab ${activeFilter === 'RED' ? 'active' : ''}`}
              onClick={() => handleFilterChange('RED')}
            >
              <span className="tab-dot tab-dot-red"></span>
              Erori
              <span className="tab-count">{statusCounts.RED}</span>
            </button>
            <button
              className={`status-tab ${activeFilter === 'SENT' ? 'active' : ''}`}
              onClick={() => handleFilterChange('SENT')}
            >
              Trimise
              <span className="tab-count">{statusCounts.SENT}</span>
            </button>
          </div>

          {/* Inline filters on the right */}
          <div className="inline-filters">
            <SearchableClientDropdown
              clients={clients}
              value={advancedFilters.clientId}
              onChange={(clientId) => setAdvancedFilters(prev => ({ ...prev, clientId }))}
              placeholder="Caută emitent..."
            />

            <div className="inline-filter-date-group">
              <input
                type="date"
                className="inline-filter-date"
                value={advancedFilters.dateFrom}
                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                placeholder="De la"
                title="De la data"
              />
              <span className="date-separator">—</span>
              <input
                type="date"
                className="inline-filter-date"
                value={advancedFilters.dateTo}
                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                placeholder="Până la"
                title="Până la data"
              />
            </div>

            {/* BNR Exchange Rate Display */}
            {bnrRate && (
              <div className="bnr-rate-display" title={`Curs BNR din ${bnrRate.date || 'N/A'} (ziua precedentă)`}>
                <span className="bnr-rate-text">
                  {bnrRate.is_default ? 'curs BNR indisponibil' : `${bnrRate.rate?.toFixed(4)} EUR/RON`}
                </span>
              </div>
            )}

            {hasActiveFilters && (
              <button
                className="inline-filter-clear"
                onClick={() => setAdvancedFilters({ clientId: '', dateFrom: '', dateTo: '' })}
                title="Resetează filtrele"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="validation-main">
        {renderContent()}
      </main>

      {/* DISABLED: Bulk validation carousel - see import comment for details
      {showValidationCarousel && (
        <ValidationCarouselModal
          onClose={handleCloseValidationCarousel}
          onComplete={handleValidationComplete}
        />
      )}
      */}
    </div>
  );
}
